import type { App, Media } from '@/payload-types'

type AppChipInput = {
  name: string
  icon: App['icon']
  glyph: App['glyph']
  backgroundColor: string
}

function resolveMediaUrl(media: App['icon'] | App['glyph']): string | undefined {
  if (media && typeof media === 'object' && 'url' in media) {
    return media.url ?? undefined
  }
  return undefined
}

// Builds a chip HTMLElement for an app mention inside the search bar's
// contenteditable input. Uses plain DOM instead of `renderToString` so the
// chip never depends on React context or hooks in `AppIcon`.
export function createAppChipElement({
  name,
  icon,
  glyph,
  backgroundColor,
}: AppChipInput): HTMLElement {
  const chip = document.createElement('div')
  chip.contentEditable = 'false'
  chip.className = 'inline-flex items-center'

  // Inner layout (matches former AppTemplate)
  const inner = document.createElement('div')
  inner.className = 'flex items-center pl-0.5'

  // Vertical alignment spacer
  const spacer = document.createElement('div')
  spacer.className = 'h-2.5 w-0'

  // Icon container (matches AppIcon outer div with default variants)
  const iconContainer = document.createElement('div')
  iconContainer.className =
    'relative shrink-0 overflow-hidden outline outline-[0.75px] outline-white/0 squircle'
  iconContainer.style.backgroundColor = backgroundColor
  iconContainer.style.width = '20px'
  iconContainer.style.height = '20px'

  // Border overlay (matches iconBorderVariants defaults: invert=false, circular=false)
  const border = document.createElement('div')
  border.className = 'absolute z-20 h-full w-full shadow-icon squircle'

  // Centering container
  const center = document.createElement('div')
  center.className = 'absolute inset-0 flex items-center justify-center'

  // Glyph/icon wrapper with positioning
  const glyphWrapper = document.createElement('div')
  glyphWrapper.style.position = 'relative'

  const glyphMedia = glyph && typeof glyph === 'object' ? (glyph as Media) : null
  const hasGlyphPositioning =
    glyphMedia &&
    (glyphMedia.coveragePercent !== undefined ||
      glyphMedia.xOffsetPercent !== undefined ||
      glyphMedia.yOffsetPercent !== undefined)

  if (hasGlyphPositioning) {
    glyphWrapper.style.width = glyphMedia.coveragePercent
      ? `${glyphMedia.coveragePercent}%`
      : '100%'
    glyphWrapper.style.height = glyphMedia.coveragePercent
      ? `${glyphMedia.coveragePercent}%`
      : '100%'

    const xOffset = glyphMedia.xOffsetPercent ?? 0
    const yOffset = glyphMedia.yOffsetPercent ?? 0
    if (xOffset !== 0 || yOffset !== 0) {
      glyphWrapper.style.transform = `translate(${xOffset}%, ${yOffset}%)`
    }
  } else {
    glyphWrapper.style.width = '100%'
    glyphWrapper.style.height = '100%'
  }

  // Image (prefer glyph URL, fall back to icon URL)
  const imgUrl = resolveMediaUrl(glyph) ?? resolveMediaUrl(icon)

  if (imgUrl) {
    const img = document.createElement('img')
    img.src = imgUrl
    img.alt = name
    img.loading = 'lazy'
    img.style.width = '100%'
    img.style.height = '100%'
    img.style.objectFit = 'contain'
    glyphWrapper.appendChild(img)
  }

  // Label
  const label = document.createElement('span')
  label.className = 'ml-1.5 text-16 font-medium leading-none'
  label.textContent = name

  // Assemble
  center.appendChild(glyphWrapper)
  iconContainer.append(border, center)
  inner.append(spacer, iconContainer, label)
  chip.appendChild(inner)

  return chip
}

// Builds the collection chip (former <CollectionTemplate>) with plain DOM so the
// search bar never pulls react-dom/server's renderToString into the client bundle.
export function createCollectionChipElement({
  triggerKey,
  label,
}: {
  triggerKey: string
  label: string
}): HTMLElement {
  const chip = document.createElement('div')
  chip.contentEditable = 'false'
  chip.className = 'inline-flex items-center'

  const inner = document.createElement('div')
  inner.className = 'mr-1 flex h-fit text-16 leading-none'

  const trigger = document.createElement('span')
  trigger.className = 'text-gray-400'
  trigger.textContent = triggerKey

  const name = document.createElement('span')
  name.className = 'text-gray-950 dark:text-white'
  name.setAttribute('contenteditable', 'false')
  name.textContent = label

  inner.append(trigger, name)
  chip.appendChild(inner)

  return chip
}

// Builds the category chip (former <CategoryTemplate>) with plain DOM.
export function createCategoryChipElement({ category }: { category: string }): HTMLElement {
  const chip = document.createElement('div')
  chip.contentEditable = 'false'
  chip.className = 'inline-flex items-center'

  const inner = document.createElement('div')
  inner.className =
    'mr-1 flex h-fit text-16 font-medium leading-none text-gray-950 dark:text-white'
  inner.setAttribute('contenteditable', 'false')
  inner.textContent = category

  chip.appendChild(inner)

  return chip
}
